<script setup lang="ts">
import { ref, computed } from 'vue'
import type { RankedResult, BacktestParams } from '../api/client'

const props = defineProps<{
  topResults: RankedResult[]
  scoreType: string
  /** Rank of the row whose Load was clicked (its result is on screen) — highlighted. */
  loadedRank?: number | null
}>()

// 2026-05-19: Load button per row so the user can copy a result row's exact
// params into the Backtest form, then Save/Apply. Before this, the user
// could only `expand` (read-only) — there was no way to materialise a row's
// params back into the form. The same OTP sweep workflow exists for the
// TPE table via loadTpeWinner; this brings parity to the focused-grid /
// optimize-wf-overlay path that feeds this table.
const emit = defineEmits<{
  (e: 'load', params: BacktestParams, row: RankedResult): void
}>()

const expandedRank = ref<number | null>(null)
const sortCol = ref<string>('rank')
const sortAsc = ref(true)

function toggleExpand(rank: number) {
  expandedRank.value = expandedRank.value === rank ? null : rank
}

// Find params that vary across top results
const varyingParams = computed(() => {
  if (props.topResults.length < 2) return [] as string[]
  // 2026-10-06: the step-aside band (BGA pure-mode grid) and VMG/reserve are swept axes too.
  const paramKeys = ['stepAsideThresholdBps', 'stepAsideBuyThresholdBps', 'zWindow', 'tiltBps', 'vixScale',
    'vixMomGate', 'termStructure', 'otpReservePct',
    'nfpFilter', 'holidayFilter', 'opexFilter', 'quadWitchingFilter',
    'futuresRollFilter', 'eomInterestFilter', 'shortTermMr'] as const
  return paramKeys.filter(k => {
    const vals = new Set(props.topResults.map(r => String((r.params as any)[k])))
    return vals.size > 1
  }) as string[]
})

// Walk-forward grids stitch every fold's test slice into an out-of-sample result (2026-10-06).
const hasOos = computed(() => props.topResults.some(r => r.oosSharpe != null))

const sortedResults = computed(() => {
  const arr = [...props.topResults]
  const col = sortCol.value
  arr.sort((a, b) => {
    let va: number, vb: number
    if (col === 'rank') { va = a.rank; vb = b.rank }
    else if (col === 'fitness') { va = a.fitness; vb = b.fitness }
    else if (col === 'sharpe') { va = a.fullResult.sharpeRatio; vb = b.fullResult.sharpeRatio }
    else if (col === 'cagr') { va = a.fullResult.cagrPct; vb = b.fullResult.cagrPct }
    else if (col === 'maxdd') { va = a.fullResult.maxDrawdownPct; vb = b.fullResult.maxDrawdownPct }
    else if (col === 'winrate') { va = a.fullResult.winRate; vb = b.fullResult.winRate }
    else if (col === 'cyclewinrate') { va = a.fullResult.cycleWinRate; vb = b.fullResult.cycleWinRate }
    else if (col === 'trades') { va = a.fullResult.tradeCount; vb = b.fullResult.tradeCount }
    else if (col === 'oossharpe') { va = a.oosSharpe ?? -Infinity; vb = b.oosSharpe ?? -Infinity }
    else if (col === 'ooscagr') { va = a.oosCagr ?? -Infinity; vb = b.oosCagr ?? -Infinity }
    else { va = a.rank; vb = b.rank }
    return sortAsc.value ? va - vb : vb - va
  })
  return arr
})

function toggleSort(col: string) {
  if (sortCol.value === col) sortAsc.value = !sortAsc.value
  else { sortCol.value = col; sortAsc.value = col === 'rank' }
}

function fmtPct(v: number): string {
  return (v * 100).toFixed(1) + '%'
}

function fmtDec(v: number, d = 2): string {
  return v.toFixed(d)
}

function valClass(v: number): string {
  if (v > 0) return 'positive'
  if (v < 0) return 'negative'
  return ''
}

function paramLabel(key: string): string {
  const labels: Record<string, string> = {
    zWindow: 'Z', tiltBps: 'Tilt', vixScale: 'VixS', termStructure: 'TS',
    nfpFilter: 'NFP', holidayFilter: 'Hol', opexFilter: 'OpEx',
    quadWitchingFilter: 'QW', exDividendFilter: 'ExDiv', futuresRollFilter: 'Roll', eomInterestFilter: 'EoM',
    shortTermMr: 'STMR', stepAsideThresholdBps: 'SELL bp', stepAsideBuyThresholdBps: 'BUY bp',
    vixMomGate: 'VMG', otpReservePct: 'Rsv',
  }
  return labels[key] ?? key
}

function paramVal(params: BacktestParams, key: string): string {
  const v = (params as any)[key]
  if (typeof v === 'boolean') return v ? 'Y' : 'N'
  return String(v)
}
</script>

<template>
  <div class="opt-table-wrap">
    <div class="section-label">Top {{ topResults.length }} Results</div>
    <table class="opt-table">
      <thead>
        <tr>
          <th @click="toggleSort('rank')" class="sortable">#</th>
          <th @click="toggleSort('fitness')" class="sortable">{{ scoreType === 'fitness' ? 'Fitness' : 'Sharpe' }}</th>
          <th @click="toggleSort('sharpe')" class="sortable">Sharpe</th>
          <th>DSR</th>
          <th @click="toggleSort('cagr')" class="sortable">CAGR</th>
          <th @click="toggleSort('maxdd')" class="sortable">Max DD</th>
          <th @click="toggleSort('winrate')" class="sortable" title="Per-day Win%: fraction of trading days with positive return.">Win%</th>
          <th @click="toggleSort('cyclewinrate')" class="sortable" title="Per-cycle Win%: fraction of complete BUY→SELL round-trips with positive P&L.">Cycle Win%</th>
          <th @click="toggleSort('trades')" class="sortable">Trades</th>
          <template v-if="hasOos">
            <th @click="toggleSort('oossharpe')" class="sortable" title="Walk-forward out-of-sample: every fold's test slice stitched together.">OOS Sharpe</th>
            <th @click="toggleSort('ooscagr')" class="sortable" title="Walk-forward out-of-sample CAGR (test slices stitched, 252-day annualised).">OOS CAGR</th>
          </template>
          <th v-for="p in varyingParams" :key="p">{{ paramLabel(p) }}</th>
          <th></th>
        </tr>
      </thead>
      <tbody>
        <template v-for="r in sortedResults" :key="r.rank">
          <tr @click="toggleExpand(r.rank)" :class="{ 'row-expanded': expandedRank === r.rank, 'row-loaded': loadedRank === r.rank }" class="result-row" :data-testid="loadedRank === r.rank ? 'opt-row-loaded' : undefined">
            <td>{{ r.rank }}</td>
            <td class="mono">{{ fmtDec(r.fitness, 4) }}</td>
            <td class="mono" :class="valClass(r.fullResult.sharpeRatio)">{{ fmtDec(r.fullResult.sharpeRatio) }}</td>
            <td class="mono" :style="{ color: r.fullResult.deflatedSharpeRatio > 2 ? '#4caf50' : r.fullResult.deflatedSharpeRatio >= 1 ? '#fbc02d' : '#f44336' }">
              {{ r.fullResult.deflatedSharpeRatio?.toFixed(2) ?? '—' }}
            </td>
            <td class="mono" :class="valClass(r.fullResult.cagrPct)">{{ fmtPct(r.fullResult.cagrPct) }}</td>
            <td class="mono negative">{{ fmtPct(r.fullResult.maxDrawdownPct) }}</td>
            <td class="mono">{{ fmtPct(r.fullResult.winRate) }}</td>
            <td class="mono">{{ fmtPct(r.fullResult.cycleWinRate) }}</td>
            <td class="mono">{{ r.fullResult.tradeCount }}</td>
            <template v-if="hasOos">
              <td class="mono" data-testid="opt-row-oos-sharpe" :class="valClass(r.oosSharpe ?? 0)">{{ r.oosSharpe != null ? fmtDec(r.oosSharpe) : '—' }}</td>
              <td class="mono" :class="valClass(r.oosCagr ?? 0)">{{ r.oosCagr != null ? fmtPct(r.oosCagr) : '—' }}</td>
            </template>
            <td v-for="p in varyingParams" :key="p" class="mono param-cell">{{ paramVal(r.params, p) }}</td>
            <td>
              <button class="btn-sm" data-testid="opt-row-load-btn"
                      title="Copy this row's params into the Backtest form (then Save/Apply)"
                      @click.stop="emit('load', r.params, r)">{{ loadedRank === r.rank ? 'Loaded' : 'Load' }}</button>
            </td>
          </tr>
          <tr v-if="expandedRank === r.rank" class="detail-row">
            <td :colspan="10 + (hasOos ? 2 : 0) + varyingParams.length">
              <div class="detail-panel">
                <div class="detail-kpis">
                  <div class="detail-kpi">
                    <span class="kpi-lbl">CAGR</span>
                    <span class="kpi-val" :class="valClass(r.fullResult.cagrPct)">{{ fmtPct(r.fullResult.cagrPct) }}</span>
                  </div>
                  <div class="detail-kpi">
                    <span class="kpi-lbl">Sharpe</span>
                    <span class="kpi-val">{{ fmtDec(r.fullResult.sharpeRatio) }}</span>
                  </div>
                  <div class="detail-kpi">
                    <span class="kpi-lbl">Max DD</span>
                    <span class="kpi-val negative">{{ fmtPct(r.fullResult.maxDrawdownPct) }}</span>
                  </div>
                  <div class="detail-kpi">
                    <span class="kpi-lbl">vs B&H</span>
                    <span class="kpi-val" :class="valClass(r.fullResult.excessReturnPct)">{{ fmtPct(r.fullResult.excessReturnPct) }}</span>
                  </div>
                </div>
                <div v-if="r.fullResult.hedgeDaysCount" class="detail-hedge">
                  Hedge: {{ r.fullResult.hedgeDaysCount }} triggers, P&L {{ fmtDec(r.fullResult.hedgeTotalPnl, 0) }}
                </div>
                <div v-if="r.fitnessBreakdown && Object.keys(r.fitnessBreakdown).length" class="detail-breakdown">
                  <span v-for="(val, key) in r.fitnessBreakdown" :key="key" class="breakdown-pill">
                    {{ key }}: {{ fmtDec(val, 3) }}
                  </span>
                </div>
              </div>
            </td>
          </tr>
        </template>
      </tbody>
    </table>
  </div>
</template>

<style scoped>
.opt-table-wrap { margin-bottom: 16px; }
.section-label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 1px; padding-bottom: 4px; border-bottom: 1px solid #222; margin-bottom: 8px; }
.opt-table { width: 100%; border-collapse: collapse; font-size: 12px; }
.opt-table th { text-align: left; padding: 6px 8px; color: #888; font-weight: 500; border-bottom: 1px solid #333; font-size: 10px; text-transform: uppercase; }
.opt-table td { padding: 5px 8px; border-bottom: 1px solid #1a1a1a; color: #ccc; }
.sortable { cursor: pointer; }
.sortable:hover { color: #eee; }
.result-row { cursor: pointer; }
.result-row:hover { background: #222; }
.row-expanded { background: #1a2a1a; }
.row-loaded td { background: #1c2a3a; }
.row-loaded td:first-child { box-shadow: inset 3px 0 0 #58a6ff; }
.mono { font-family: monospace; }
.positive { color: #4caf50; }
.negative { color: #f44336; }
.param-cell { color: #888; font-size: 11px; }
.detail-row td { padding: 0; }
.detail-panel { padding: 12px 16px; background: #111; border-left: 3px solid #4caf50; margin: 4px 0 8px; }
.detail-kpis { display: flex; gap: 24px; margin-bottom: 8px; }
.detail-kpi { display: flex; flex-direction: column; }
.kpi-lbl { font-size: 9px; color: #666; text-transform: uppercase; }
.kpi-val { font-family: monospace; font-size: 14px; color: #ccc; }
.detail-hedge { font-size: 11px; color: #888; margin-bottom: 6px; }
.detail-breakdown { display: flex; gap: 6px; flex-wrap: wrap; }
.breakdown-pill { padding: 2px 6px; background: #1a1a1a; border: 1px solid #333; border-radius: 8px; font-size: 10px; color: #aaa; }
</style>
